import { useEffect, useState, useMemo } from "react";
import { COLOR, FONT, SPACE, RADIUS } from "../lib/tokens";
import { useOtherOpenSteps } from "../lib/useOtherOpenSteps";

// CookOtherTimersStrip — compact horizontal scrollable strip of
// "what else is cooking" pills. Renders inside CookMode beneath
// TeamCookOverlay so the lead sees every parallel timer running
// across the team WITHOUT having to navigate between steps.
//
// One pill per row from useOtherOpenSteps:
//   <emoji-or-color> <name> · Step N · 3:42
// Pills tick locally (1Hz interval) so the countdowns stay accurate
// without DB roundtrips.
//
// Auto-hides entirely when there are zero other open rows (solo cook
// scenario) — no visual weight when there's nothing to show.
//
// Tap a pill → fires the optional onJumpToStep callback with the
// step id. CookMode wires that to setActiveStep so the lead can
// jump to a helper's step to peek or take over.

export default function CookOtherTimersStrip({
  userId,
  cookSessionId,
  currentStepId,
  onJumpToStep,
}) {
  const rows = useOtherOpenSteps({
    cookSessionId,
    viewerUserId: userId,
    currentStepId,
  });

  // 1Hz tick so the countdowns refresh visibly. Skip the interval
  // when there's nothing to render.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (rows.length === 0) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [rows.length]);

  if (rows.length === 0) return null;

  // Header copy is row-set aware. When every pill belongs to the
  // viewer (solo cook with multiple parallel timers running on
  // OTHER steps), reading "ALSO COOKING" is misleading — there's no
  // "also." Reframe to "OTHER TIMERS" when it's all self, keep
  // "ALSO COOKING" when teammates are in the mix.
  const allSelf = rows.length > 0 && rows.every((r) => r.user_id === userId);
  const headerLabel = allSelf ? "OTHER TIMERS" : "ALSO COOKING";

  return (
    <div style={{
      position: "sticky",
      top: 0,
      zIndex: 79, // just below TeamCookOverlay
      padding: `${SPACE.sm}px ${SPACE.section}px`,
      background: COLOR.deep,
      borderBottom: `1px solid ${COLOR.edge}`,
      overflowX: "auto",
      whiteSpace: "nowrap",
      WebkitOverflowScrolling: "touch",
    }}>
      <div style={{
        fontFamily: FONT.mono,
        fontSize: 9,
        letterSpacing: 1.4,
        color: COLOR.muted,
        marginBottom: 6,
      }}>
        {headerLabel}
      </div>
      <div style={{
        display: "flex",
        gap: 8,
        alignItems: "center",
      }}>
        {rows.map((row) => (
          <OtherTimerPill
            key={row.id}
            row={row}
            onTap={onJumpToStep ? () => onJumpToStep(row.step_id) : null}
          />
        ))}
      </div>
    </div>
  );
}

function OtherTimerPill({ row, onTap }) {
  const ts = row.timer_state || null;
  const isPaused = !!ts?.isPaused;
  const endsAtMs = ts?.endsAt ? new Date(ts.endsAt).getTime() : null;

  const phase = useMemo(() => {
    if (!ts) return "ready";
    if (ts.ended) return "ended";
    if (isPaused) return "paused";
    if (!endsAtMs) return "ready";
    return (endsAtMs - Date.now()) <= 0 ? "ended" : "ticking";
  }, [ts, isPaused, endsAtMs]);

  // Compute remaining inline so the pill ticks every render.
  let displaySec = null;
  if (phase === "ticking" && endsAtMs) {
    displaySec = Math.max(0, Math.round((endsAtMs - Date.now()) / 1000));
  } else if (phase === "paused") {
    displaySec = Math.max(0, Number(ts?.pausedRemainingSec) || 0);
  } else if (phase === "ended") {
    displaySec = 0;
  }

  const fmt = (s) => {
    if (s == null) return "ready";
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  };

  const name = (row.profile?.name || row.profile?.username || "Helper")
    .split(/\s+/)[0];

  const stepLabel = row.step_title || `Step ${row.step_id}`;

  const tone = phase === "ended" ? COLOR.leaf
             : phase === "paused" ? "#C28F2C"
             : COLOR.ink;

  const Component = onTap ? "button" : "div";

  return (
    <Component
      {...(onTap ? { onClick: onTap, type: "button" } : {})}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 12px",
        background: COLOR.cream,
        border: `1px solid ${COLOR.edge}`,
        borderRadius: RADIUS.pill || 999,
        cursor: onTap ? "pointer" : "default",
        flexShrink: 0,
      }}>
      <span style={{
        fontFamily: FONT.mono,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.6,
        color: COLOR.sub,
      }}>
        {name.toUpperCase()}
      </span>
      <span style={{
        fontFamily: FONT.detail,
        fontSize: 12,
        fontStyle: "italic",
        color: COLOR.muted,
        maxWidth: 140,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}>
        {stepLabel}
      </span>
      <span style={{
        fontFamily: FONT.mono,
        fontSize: 12,
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        color: tone,
      }}>
        {phase === "ready" ? "ready" : fmt(displaySec)}
      </span>
    </Component>
  );
}
